/**
 * React хуки для Phone Binding (привязка TG по номеру телефона)
 * Дата: 10.02.2026
 * 
 * Используется в:
 * - components/messages/TelegramLinkPanel.tsx — inline привязка из чата (TODO-021b)
 * - pages/PhoneBinding.tsx — массовая привязка (TODO-021, TODO-021a)
 * 
 * Связан с:
 * - api/phone-binding.ts — API клиент
 * - internal/handler/mass_phone_binding.go — backend handler
 * - internal/domain/service/binding/mass_phone_binding.go — сервис
 * 
 * КАК РАБОТАЕТ:
 * - usePhoneSearch() — mutation для поиска одного номера (inline привязка)
 * - usePhoneBindingStats() — query для статистики (страница /phone-binding)
 * - useBatchSearch() — mutation для запуска batch поиска
 * - useBatchJobStatus() — query для отслеживания статуса batch job
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { phoneBindingApi, type PhoneSearchResult, type SearchStats, type BatchJobStatus } from '@/api/phone-binding';
import { logger } from '@/utils/logger';

// ═══════════════════════════════════════════════════
// usePhoneSearch — поиск одного номера (inline из чата)
// ═══════════════════════════════════════════════════

export interface UsePhoneSearchOptions {
  /** Callback при успешной привязке */
  onFound?: (result: PhoneSearchResult) => void;
  /** Callback при ненайденном */
  onNotFound?: (result: PhoneSearchResult) => void;
  /** Callback при ошибке */
  onError?: (error: Error) => void;
}

/**
 * Хук для поиска одного номера телефона в Telegram
 * Вызывает POST /api/admin/phone-binding/single/search
 * 
 * @example
 * const { search, isSearching, result } = usePhoneSearch({
 *   onFound: (r) => toast.showSuccess(`Найден: @${r.telegram_username}`),
 *   onNotFound: () => toast.showWarning('Не найден'),
 * });
 * 
 * <Button onClick={() => search('79854001190')} disabled={isSearching}>
 *   Проверить в Telegram
 * </Button>
 */
export function usePhoneSearch(options: UsePhoneSearchOptions = {}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (phone: string) => {
      logger.info('phone_search_started', { phone });
      // 12.02.2026 FIX: бэкенд возвращает PhoneSearchResult напрямую
      const result = await phoneBindingApi.searchSingle(phone);
      return result;
    },
    onSuccess: (result: PhoneSearchResult) => {
      logger.info('phone_search_result', { 
        phone: result.phone, 
        status: result.status,
        telegram_user_id: result.telegram_user_id,
      });

      if (result.status === 'found') {
        // Инвалидировать привязки для обновления UI
        queryClient.invalidateQueries({ queryKey: ['bindings'] });
        queryClient.invalidateQueries({ queryKey: ['messages'] });
        options.onFound?.(result);
      } else {
        options.onNotFound?.(result);
      }
    },
    onError: (error: Error) => {
      logger.error('phone_search_error', { error: error.message });
      options.onError?.(error);
    },
  });

  return {
    /** Запустить поиск */
    search: mutation.mutate,
    /** Запустить поиск (async) */
    searchAsync: mutation.mutateAsync,
    /** Поиск в процессе */
    isSearching: mutation.isPending,
    /** Результат последнего поиска */
    result: mutation.data ?? null,
    /** Ошибка последнего поиска */
    error: mutation.error,
    /** Сбросить результат */
    reset: mutation.reset,
  };
}

// ═══════════════════════════════════════════════════
// usePhoneBindingStats — статистика поиска
// ═══════════════════════════════════════════════════

/**
 * Хук для получения статистики привязки
 * 
 * @example
 * const { stats, isLoading } = usePhoneBindingStats();
 * // stats.total_patients, stats.found_by_search, etc.
 */
export function usePhoneBindingStats() {
  const query = useQuery<SearchStats>({
    queryKey: ['phone-binding-stats'],
    queryFn: () => phoneBindingApi.getStats(),
    staleTime: 2 * 60 * 1000, // 2 минуты
  });

  return {
    stats: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// ═══════════════════════════════════════════════════
// useBatchSearch — запуск массового поиска
// ═══════════════════════════════════════════════════

/**
 * Хук для запуска batch поиска
 * 
 * @example
 * const { startBatch, isBatchRunning } = useBatchSearch();
 * startBatch({ source: 'manual', phones: selectedPhones });
 */
export function useBatchSearch() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: phoneBindingApi.startBatchSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-binding-stats'] });
    },
  });

  return {
    startBatch: mutation.mutate,
    startBatchAsync: mutation.mutateAsync,
    isBatchRunning: mutation.isPending,
    jobId: mutation.data?.job_id ?? null,
    error: mutation.error,
  };
}

// ═══════════════════════════════════════════════════
// useBatchJobStatus — статус batch задачи
// ═══════════════════════════════════════════════════

/**
 * Хук для отслеживания статуса batch job (с polling)
 * 
 * @example
 * const { job } = useBatchJobStatus(jobId);
 * // job.status, job.processed, job.found
 */
export function useBatchJobStatus(jobId: string | null) {
  const query = useQuery<BatchJobStatus>({
    queryKey: ['phone-binding-job', jobId],
    queryFn: () => phoneBindingApi.getJobStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Polling каждые 2 секунды пока job не завершён
      if (data?.status === 'completed' || data?.status === 'failed') return false;
      return 2000;
    },
  });

  return {
    job: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
